import {
  CallQueueItem,
  isPreConnectedStatus,
  MessageQueueItem,
  MessageThread,
  PhoneCall,
  PhoneCallStatus,
  PhoneCallWs,
  PopupQueueItem,
  PopupQueueItemType,
  PopupQueueItemTypes,
} from '@mangovoice/types'

const addItemToQueue = (
  queue: PopupQueueItem[],
  item: PopupQueueItem,
  type: PopupQueueItemType,
): MessageQueueItem[] | CallQueueItem[] => {
  const _id =
    type === PopupQueueItemTypes.CALL
      ? (item as PhoneCall | PhoneCallWs).callUuid
      : String((item as MessageThread).id)

  if (!_id) {
    console.error('Could not add item to queue.  Missing id.')
    return [...queue]
  }

  const newItem: PopupQueueItem = {
    ...item,
    _id,
    type,
  } as MessageQueueItem | CallQueueItem

  return [...queue, newItem] // Create a NEW array with the new item
}

export const addPhoneCallToQueue = (
  queue: PopupQueueItem[],
  call: PhoneCall,
): PopupQueueItem[] => {
  return addItemToQueue(queue, call as CallQueueItem, PopupQueueItemTypes.CALL)
}

export const addMessageToQueue = (
  queue: MessageQueueItem[],
  messages: MessageThread[],
): MessageQueueItem[] => {
  return messages.reduce(
    (newQueue, message) => {
      // Use latest_message.id for unique identification of each message
      const messageId = String(message.latest_message?.id || message.id)
      const existingMessageIndex = newQueue.findIndex(
        (item) => item._id === messageId,
      )

      const newItem: MessageQueueItem = {
        ...message,
        _id: messageId,
        type: PopupQueueItemTypes.MESSAGE,
      } as MessageQueueItem

      if (existingMessageIndex >= 0) {
        // Update existing message in place to prevent duplicates
        const updatedQueue = [...newQueue]
        updatedQueue[existingMessageIndex] = newItem
        return updatedQueue
      } else {
        // Add new message to queue
        return [...newQueue, newItem]
      }
    },
    [...queue],
  )
}

export const removeItemFromQueueById = (
  queue: PopupQueueItem[],
  id: string,
): PopupQueueItem[] => {
  return queue.filter((item) => item._id !== id)
}

export const removeItemFromQueueByType = (
  queue: PopupQueueItem[],
  type: PopupQueueItemType,
): PopupQueueItem[] => {
  return queue.filter((item) => item.type !== type)
}

export const removeMessagesFromQueueByPhoneNumber = (
  queue: PopupQueueItem[],
  phoneNumber: string,
): PopupQueueItem[] => {
  return queue.filter((item) => {
    // Only filter message items
    if (item.type !== PopupQueueItemTypes.MESSAGE) {
      return true
    }
    // Remove messages with matching phone number
    const messageItem = item as MessageQueueItem
    return messageItem.external_phone_number !== phoneNumber
  })
}

export const queueContainsCall = (queue: PopupQueueItem[]): boolean => {
  return queue.some((item) => item.type === PopupQueueItemTypes.CALL)
}

/**
 * Gets the active item from a queue based on the queue type and business rules
 * @param items - The queue items to search through
 * @param type - The type of queue (CALL or MESSAGE)
 * @returns The active item or null if no active item is found
 */
export const getActiveQueueItem = (
  items: PopupQueueItem[],
  type: PopupQueueItemType,
): PopupQueueItem | null => {
  if (items.length === 0) {
    return null
  }

  if (type === PopupQueueItemTypes.MESSAGE) {
    // Messages: show newest (last in array)
    return items[items.length - 1]
  }

  // Calls: show RINGING and CONNECTED calls
  // Sort by callUuid to ensure consistent selection when multiple calls are active
  const activeCalls = items
    .filter(
      (item) =>
        item.type === PopupQueueItemTypes.CALL &&
        (isPreConnectedStatus((item as any).status) ||
          (item as any).status === PhoneCallStatus.CONNECTED),
    )
    .sort((a, b) => (a as any).callUuid.localeCompare((b as any).callUuid))

  if (activeCalls.length > 0) {
    return activeCalls[0] // First active call (stable sort)
  }

  return null // No active calls
}
